import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Check, Sparkles, Coins } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const TIER_STYLE = {
  GRATIS: { ring: "border-stone-300", title: "text-stone-700", btn: "bg-stone-600 hover:bg-stone-700 text-white", badge: "bg-stone-50 text-stone-700" },
  BRONZE: { ring: "border-orange-300", title: "text-orange-900", btn: "bg-orange-500 hover:bg-orange-600 text-white", badge: "bg-orange-50 text-orange-900" },
  PRATA:  { ring: "border-slate-300", title: "text-slate-900", btn: "bg-slate-700 hover:bg-slate-800 text-white", badge: "bg-slate-50 text-slate-800" },
  OURO:   { ring: "border-amber-400 ouro-glow", title: "text-amber-900", btn: "bg-amber-500 hover:bg-amber-600 text-amber-950 font-bold", badge: "bg-amber-50 text-amber-900" },
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/payments/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const onSubscribe = (tier) => {
    if (!user) return navigate("/register");
    if (user.role !== "MERCHANT" && user.role !== "ADMIN") {
      navigate("/register");
      return;
    }
    navigate("/merchant", { state: { upgradeTo: tier } });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold">Planos para comerciantes</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-stone-900 mt-2">Escolha o plano ideal</h1>
        <p className="text-stone-600 mt-4 max-w-2xl mx-auto">
          Aumente a visibilidade do seu negócio e participe do ecossistema de cashback Morar Bem.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((p) => {
          const style = TIER_STYLE[p.tier] || TIER_STYLE.BRONZE;
          const isOuro = p.tier === "OURO";
          const isGratis = p.tier === "GRATIS";
          const priceLabel = p.price === 0 ? "Grátis" : `R$ ${p.price.toFixed(2)}`;
          return (
            <Card key={p.tier} className={`p-8 rounded-3xl bg-white shadow-md border-2 ${style.ring} flex flex-col ${isOuro ? "lg:scale-105 shadow-xl" : ""}`} data-testid={`plan-card-${p.tier}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className={`font-display text-2xl font-bold ${style.title}`}>{p.display_name || p.tier}</h3>
                {isOuro && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-900"><Sparkles size={11}/> Recomendado</span>}
              </div>
              <p className="text-sm text-stone-500 min-h-[40px]">{p.description}</p>
              <div className="mt-5">
                <span className="font-display text-4xl font-bold text-stone-900">{priceLabel}</span>
                {!isGratis && <span className="text-sm text-stone-500 ml-1">/ vigência</span>}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                {p.unlimited ? "Ilimitado" : `${Math.round((p.duration_days || 0) / 30)} meses`}
                {p.cashback && <span className="ml-2 inline-flex items-center gap-1 text-blue-900"><Coins size={11}/> 5% cashback ativo</span>}
                {(p.signup_bonus || 0) > 0 && <span className="ml-2 inline-flex items-center gap-1 text-emerald-700">+ R$ {p.signup_bonus.toFixed(2)} de bônus inicial</span>}
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-stone-700">
                    <Check size={16} className="text-blue-800 mt-0.5 shrink-0"/> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => onSubscribe(p.tier)} className={`mt-6 rounded-xl h-11 ${style.btn}`} data-testid={`subscribe-${p.tier}`}>
                {user?.role === "MERCHANT" ? `Assinar ${p.display_name || p.tier}` : "Quero ser comerciante"}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
